import { useReactTable } from "@tanstack/react-table";
import React from "react";
import type { Payment } from "@aidkitorg/aidkit/lib/admin_panel/payments";
import { Button, Checkbox, Select, Slider } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";
import { useLocalizedStrings } from "../../../Localization";
import type { PaymentsOverview } from "@aidkitorg/types/lib/survey";

export type PaymentsFilterProps = {
  table: ReturnType<typeof useReactTable<Payment>>
  maxBatchTotalAmount: number,
  setActiveFilters: React.Dispatch<React.SetStateAction<number>>
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  defaultTableColumns?: PaymentsOverview['columns']
}

export type Filters = {
  paymentEnabledStatus: string,
  // notifications: string,
  firstPaymentStart: string,
  firstPaymentEnd: string,
  lastPaymentStart: string,
  lastPaymentEnd: string,
  hasFailedPayments: boolean,
  batchTotalAmountRange: [number, number],
}

export const PaymentsFilter: React.FC<PaymentsFilterProps> = ({
  table,
  maxBatchTotalAmount,
  setActiveFilters,
  filters,
  setFilters,
  defaultTableColumns
}) => {
  const L = useLocalizedStrings()

  // Determine which filters to show based on config
  const showPaymentStatusFilter = defaultTableColumns?.paymentStatus === 'visibleAndFilterable';
  const showFailedFilter = defaultTableColumns?.failed === 'visibleAndFilterable';
  const showTotalFilter = defaultTableColumns?.total === 'visibleAndFilterable';
  const showFirstPaymentDateFilter = defaultTableColumns?.firstPaymentDate === 'visibleAndFilterable';
  const showLastPaymentDateFilter = defaultTableColumns?.lastPaymentDate === 'visibleAndFilterable';
  
  const applyFilters = () => {
    let activeFilters = 0;

    const paymentEnabledStatusCol = table.getColumn("paymentEnabledStatus")
    // const notificationsCol = table.getColumn("notifications")
    const hasFailedPaymentsCol = table.getColumn("failed")

    if (filters.paymentEnabledStatus !== "") activeFilters++
    paymentEnabledStatusCol?.setFilterValue(filters.paymentEnabledStatus || undefined)

    // if (filters.notifications !== "") activeFilters++
    // notificationsCol?.setFilterValue(filters.notifications || undefined)

    if (filters.hasFailedPayments !== false) activeFilters++
    hasFailedPaymentsCol?.setFilterValue(filters.hasFailedPayments || undefined)

    const firstPaymentDateCol = table.getColumn("firstPaymentDate")

    if (filters.firstPaymentStart !== "" || filters.firstPaymentEnd !== "") activeFilters++
    firstPaymentDateCol?.setFilterValue(
      filters.firstPaymentStart || filters.firstPaymentEnd
        ? { firstPaymentStart: filters.firstPaymentStart, firstPaymentEnd: filters.firstPaymentEnd }
        : undefined
    )

    const lastPaymentDateCol = table.getColumn("lastPaymentDate")

    if (filters.lastPaymentStart !== "" || filters.lastPaymentEnd !== "") activeFilters++
    lastPaymentDateCol?.setFilterValue(
      filters.lastPaymentStart || filters.lastPaymentEnd
        ? { lastPaymentStart: filters.lastPaymentStart, lastPaymentEnd: filters.lastPaymentEnd }
        : undefined
    )

    if (filters.batchTotalAmountRange[0] !== 0 || filters.batchTotalAmountRange[1] !== maxBatchTotalAmount) activeFilters++
    const batchTotalAmount = table.getColumn("totalAmount")
    batchTotalAmount?.setFilterValue(filters.batchTotalAmountRange)

    setActiveFilters(activeFilters)
  }

  const resetFilters = () => {
    setFilters({
      paymentEnabledStatus: "",
      // notifications: "",
      firstPaymentStart: "",
      firstPaymentEnd: "",
      lastPaymentStart: "",
      lastPaymentEnd: "",
      hasFailedPayments: false,
      batchTotalAmountRange: [0, maxBatchTotalAmount]
    })
    setActiveFilters(0)
    table.resetColumnFilters()
  }

  const PAYMENT_STATUS_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: L.admin_panel.payments.all_statuses,
    }, 
    {
      value: "enabled",
      label: L.admin_panel.payments.enabled,
    },
    {
      value: "disabled",
      label: L.admin_panel.payments.disabled
    }
  ]

  const getPaymentStatusOption = (value: string) => (PAYMENT_STATUS_FILTER_VALUES.find((v) => v.value === value))
  // const getNotificationOption = (value: string) => (NOTIFICATONS_STATUS_VALUES.find((v) => v.value === value))

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        {showPaymentStatusFilter && (
          <div>
            <Select
              className="h-10"
              options={PAYMENT_STATUS_FILTER_VALUES}
              description={L.admin_panel.payments.payment_status_desc}
              label={L.admin_panel.payments.payment_status}
              labelLocation="above"
              value={getPaymentStatusOption(filters.paymentEnabledStatus)}
              onChange={(value) => setFilters(f => ({... f, paymentEnabledStatus: value.value}))}
            />
          </div>
        )}

        {showFirstPaymentDateFilter && (
          <div>
            <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.payments.first_payment_date}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label={L.admin_panel.payments.first_payment_date_from}
                type="date"
                value={filters.firstPaymentStart}
                onChange={(e) => setFilters(f => ({ ...f, firstPaymentStart: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm"
              />
              <span className="text-gray-700">to</span>
              <input
                aria-label={L.admin_panel.payments.first_payment_date_to}
                type="date"
                value={filters.firstPaymentEnd}
                onChange={(e) => setFilters(f => ({ ...f, firstPaymentEnd: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm"
              />
            </div>
          </div>
        )}

        {showLastPaymentDateFilter && (
          <div>
            <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.payments.last_payment_date}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label={L.admin_panel.payments.last_payment_date_from}
                type="date"
                value={filters.lastPaymentStart}
                onChange={(e) => setFilters(f => ({ ...f, lastPaymentStart: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
              />
              <span className="text-gray-700">to</span>
              <input
                aria-label={L.admin_panel.payments.last_payment_date_to}
                type="date"
                value={filters.lastPaymentEnd}
                onChange={(e) => setFilters(f => ({ ...f, lastPaymentEnd: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
              />
            </div>
          </div>
        )}
      </div>

      {showTotalFilter && (
        <div className="pt-4 max-w-96">
          <div className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.payments.batch_total_amount}</div>
          <Slider
            label={L.admin_panel.payments.batch_total_amount}
            isRange
            min={0}
            max={maxBatchTotalAmount}
            value={filters.batchTotalAmountRange}
            onChange={(v) => {
              if (Array.isArray(v) && v.length == 2) {
                setFilters(f => ({...f, batchTotalAmountRange: v}))
              }
            }}
            isCurrency={true}
            currencySymbol="$"
          />
        </div>
      )}

      {showFailedFilter && (
        <div className="pt-4">
          <Checkbox checked={filters.hasFailedPayments} onChange={(checked) => { setFilters((f) => ({ ...f, hasFailedPayments: checked ? true : false })) }} label={L.admin_panel.payments.has_failed}/>
        </div>
      )}

      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>{L.admin_panel.payments.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.payments.apply_filters}</Button>
        </div>
      </div>
    </div>
  )
}